import React, { useState, useContext } from 'react';
import {
  Box,
  Button,
  CircularProgress,
  FormControl,
  FormControlLabel,
  FormLabel,
  Radio,
  RadioGroup,
  Typography,
  Alert,
} from '@mui/material';
import axios from 'axios';
import { API_ENDPOINT } from '../../../configurations/global.config';
import { AuthContext } from '../../../contexts/user-context/user-context';
import { OrderType, RerouteMode } from '../../../interfaces/delivery-company/orders';
import { BranchType } from '../../../interfaces/delivery-company/branches';
import { useSnackBar } from '../../../contexts/snackbar';
import AutocompleteWithThrottle from '../autocomplete-with-throttle/autocomplete-with-throttle';
import { DeliveryState } from '../../../enums/orders';
import AltRouteIcon from '@mui/icons-material/AltRoute';
import LocalShippingIcon from '@mui/icons-material/LocalShipping';
import SwapHorizIcon from '@mui/icons-material/SwapHoriz';

interface RerouteOrdersProps {
  selectedOrders: Map<string, OrderType>;
  onFinishAction: () => void;
  refetchOrders: () => void;
}

/**
 * Bulk action component for rerouting orders.
 * Renders content directly to be used within the parent bulk action dialog.
 */
function RerouteOrdersAction({
  selectedOrders,
  onFinishAction,
  refetchOrders,
}: RerouteOrdersProps) {
  const { config } = useContext(AuthContext);
  const { showMessage } = useSnackBar();

  const orders = Array.from(selectedOrders.values());
  // Filter to only AT_WAREHOUSE orders for rerouting
  const eligibleOrders = orders.filter(o => o.delivery_state === DeliveryState.AT_WAREHOUSE);
  const ineligibleCount = orders.length - eligibleOrders.length;

  const [rerouteMode, setRerouteMode] = useState<RerouteMode>(RerouteMode.DIRECT_DELIVERY);
  const [targetBranch, setTargetBranch] = useState<BranchType | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleFetchBranches = async (query: string): Promise<BranchType[]> => {
    try {
      const response = await axios.get<{
        count: number;
        results: BranchType[];
      }>(`${API_ENDPOINT}/api/v1/branches/?name=${encodeURIComponent(query)}`, config);
      return response.data.results;
    } catch (e) {
      console.error('Failed to fetch branches:', e);
      return [];
    }
  };

  const handleSubmit = async () => {
    if (rerouteMode === RerouteMode.TRANSFER_TO_BRANCH && !targetBranch) {
      setError('Please select a target branch');
      return;
    }

    if (eligibleOrders.length === 0) {
      setError('No eligible orders to reroute. Orders must be at warehouse.');
      return;
    }

    setIsLoading(true);
    setError(null);

    try {
      const requestData: {
        order_ids: string[];
        reroute_mode: RerouteMode;
        target_branch_id?: number | string;
      } = {
        order_ids: eligibleOrders.map((o) => o.order_id),
        reroute_mode: rerouteMode,
      };

      if (rerouteMode === RerouteMode.TRANSFER_TO_BRANCH && targetBranch) {
        requestData.target_branch_id = targetBranch.id;
      }

      await axios.post(
        `${API_ENDPOINT}/api/v1/orders/reroute/`,
        requestData,
        config
      );

      showMessage(
        `Successfully rerouted ${eligibleOrders.length} order${eligibleOrders.length > 1 ? 's' : ''}`
      );
      refetchOrders();
      onFinishAction();
    } catch (e) {
      if (axios.isAxiosError(e)) {
        const errorMessage = e.response?.data?.error || 'Failed to reroute orders';
        setError(errorMessage);
      } else {
        setError('An unexpected error occurred');
      }
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <Box>
      <Typography align="center" sx={{ mb: 2 }}>
        {eligibleOrders.length} of {orders.length} order{orders.length > 1 ? 's' : ''} eligible for rerouting
      </Typography>

      {ineligibleCount > 0 && (
        <Alert severity="warning" sx={{ mb: 2 }}>
          {ineligibleCount} order{ineligibleCount > 1 ? 's are' : ' is'} not at warehouse and will be skipped.
        </Alert>
      )}

      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      <FormControl component="fieldset" fullWidth sx={{ mb: 2 }}>
        <FormLabel component="legend" sx={{ mb: 1 }}>
          Choose reroute option:
        </FormLabel>
        <RadioGroup
          value={rerouteMode}
          onChange={(e) => setRerouteMode(e.target.value as RerouteMode)}
        >
          <FormControlLabel
            value={RerouteMode.DIRECT_DELIVERY}
            control={<Radio />}
            label={
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <LocalShippingIcon fontSize="small" color="action" />
                <Box>
                  <Typography variant="body1">
                    Deliver directly from current branch
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    Skip any pending transfers and deliver to customer from current location
                  </Typography>
                </Box>
              </Box>
            }
            sx={{
              mb: 1,
              ml: 0,
              p: 1.5,
              border: '1px solid',
              borderColor: rerouteMode === RerouteMode.DIRECT_DELIVERY ? 'primary.main' : 'divider',
              borderRadius: 1,
              backgroundColor: rerouteMode === RerouteMode.DIRECT_DELIVERY ? 'action.selected' : 'transparent',
            }}
          />
          <FormControlLabel
            value={RerouteMode.TRANSFER_TO_BRANCH}
            control={<Radio />}
            label={
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <SwapHorizIcon fontSize="small" color="action" />
                <Box>
                  <Typography variant="body1">
                    Transfer to another branch, then deliver
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    Forward to a specific branch for last-mile delivery
                  </Typography>
                </Box>
              </Box>
            }
            sx={{
              mb: 1,
              ml: 0,
              p: 1.5,
              border: '1px solid',
              borderColor: rerouteMode === RerouteMode.TRANSFER_TO_BRANCH ? 'primary.main' : 'divider',
              borderRadius: 1,
              backgroundColor: rerouteMode === RerouteMode.TRANSFER_TO_BRANCH ? 'action.selected' : 'transparent',
            }}
          />
        </RadioGroup>
      </FormControl>

      {rerouteMode === RerouteMode.TRANSFER_TO_BRANCH && (
        <Box sx={{ mb: 2 }}>
          <AutocompleteWithThrottle
            label="Select Target Branch"
            required
            fetch={handleFetchBranches}
            value={targetBranch}
            fetchAllOnLoad
            getOptionLabel={(option: BranchType) => option.name}
            onChange={(e, value) => setTargetBranch(value)}
          />
        </Box>
      )}

      <Button
        variant="contained"
        onClick={handleSubmit}
        disabled={isLoading || eligibleOrders.length === 0 || (rerouteMode === RerouteMode.TRANSFER_TO_BRANCH && !targetBranch)}
        startIcon={isLoading ? <CircularProgress size={16} /> : <AltRouteIcon />}
        fullWidth
      >
        {isLoading ? 'Rerouting...' : `Reroute ${eligibleOrders.length} Order${eligibleOrders.length !== 1 ? 's' : ''}`}
      </Button>
    </Box>
  );
}

export default RerouteOrdersAction;
